import { useEffect, useState } from 'react'
import { OrgCard } from '../../planning/OrgCard'
import { GetAnnualPlanAndOrgName, GetPlanOrganizationsForAnnualPlan } from '../../../../services/AnnualPlanService'
import { type OrganizationOption } from '../../../../models/requestObjects/CommonObjects'
import { Box, CircularProgress, Stack, Typography } from '@mui/material'
import { useAnnualPlanUserRole } from '../../../../hooks/use-annualPlanUserRole'
import { AnnualPlanRole } from '../../../../models/AnnualPlanRole'
import { generatePath, useNavigate, useParams } from 'react-router-dom'
import { AppRoutes } from '../../../Routes'
import { ImplementationDetailsTabs } from '../ImplementationDetails'
import { BlueButton } from '../../../customComponents/buttons/BlueButton'
import { ArrowForward } from '@mui/icons-material'
import { handleApiResponse } from '../../../../utils/apiHelpers'

export const ProgressReportsOverview = (): JSX.Element => {
  const [isds, setIsds] = useState<OrganizationOption[]>([])
  const [loading, setLoading] = useState(true)
  const [rcnPlanOrgId, setRcnPlanOrgId] = useState<number | null>(null)

  const { rcnId, planId } = useParams()
  const annualPlanId = planId != null ? Number(planId) : 0
  const { user, userRole, isLoading: roleLoading } = useAnnualPlanUserRole(planId != null ? Number(planId) : 0)
  const nav = useNavigate()

  useEffect(() => {
    const loadData = async (): Promise<void> => {
      if (roleLoading || userRole == null) return

      const isPlanContributor = userRole === AnnualPlanRole.PlanContributor

      if (isPlanContributor) {
        if (user?.currentOrgId == null) return

        const planOrgs = await handleApiResponse(GetPlanOrganizationsForAnnualPlan(annualPlanId))
        if (planOrgs == null) return

        const response = await handleApiResponse(GetAnnualPlanAndOrgName(
          annualPlanId.toString(),
          user?.currentOrgId?.toString() ?? ''
        ))
        if (response == null) return

        const selectedIsdPlanOrg = planOrgs.find((po) => po.name === response.organizationName)
        navigateToOrganizationReports(selectedIsdPlanOrg?.id ?? 0)
      } else {
        GetPlanOrganizationsForAnnualPlan(annualPlanId, true)
          .then(({ response }) => { setRcnPlanOrgId(response[0].id) })
          .catch((error) => { console.error('Error fetching ISDs:', error) })
          .finally(() => { setLoading(false) })

        GetPlanOrganizationsForAnnualPlan(annualPlanId)
          .then(({ response }) => { setIsds(response) })
          .catch((error) => { console.error('Error fetching ISDs:', error) })
          .finally(() => { setLoading(false) })
      }
    }

    void loadData()
  }, [annualPlanId, user?.currentOrgId, userRole, roleLoading])

  const navigateToOrganizationReports = (planOrganizationId: number): void => {
    if (rcnId == null || planId == null) return

    const path = generatePath(AppRoutes.IMPLEMENTATION_PROGRESS_REPORTS_ORGANIZATION, {
      rcnId,
      planId,
      tab: String(ImplementationDetailsTabs.ProgressReports),
      planOrgId: planOrganizationId.toString()
    })
    nav(path)
  }

  if (loading || roleLoading) {
    return <CircularProgress sx={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)' }} />
  }

  return (
    <Stack data-testid="progress-reports-overview" sx={{
      display: 'flex',
      gap: '40px'
    }}
    >
      {[AnnualPlanRole.PlanAdministrator, AnnualPlanRole.PlanReviewer].includes(userRole ?? AnnualPlanRole.PlanContributor) &&
        <BlueButton sx={{ width: '200px', textAlign: 'left' }}onClick={() => { navigateToOrganizationReports(rcnPlanOrgId ?? 0) }} dataTestId='goToRcnReportButton'>
          View RCN Reports <ArrowForward sx={{ ml: '8px' }} />
        </BlueButton>
      }
      <Box data-testid="isd-midyear" sx={{
        display: 'flex',
        flexWrap: 'wrap',
        gap: '44px'
      }}
      >
        <Typography variant='h3' sx={{ width: '100%' }}>ISDs</Typography>
        {isds.map((isd) => (
          <OrgCard key={isd.name} name={isd.name} buttonText="View Reports" onClick={() => { navigateToOrganizationReports(isd.id) }} />
        ))}

      </Box>
    </Stack>
  )
}
